import { defineStore } from 'pinia'
import { api, ApiError } from '@/lib/api'
import { atRisk, daysLeft } from '@/lib/deadline'
import { useToast } from '@/composables/useToast'
import { useNotificationsStore } from '@/stores/notifications'
import type { Pursuit, Stage, Template, WorkflowProgressEvent } from '@/lib/types'

export interface TodaySections {
  overdue: Pursuit[]
  dueToday: Pursuit[]
  actionNeeded: Pursuit[]
  dueThisWeek: Pursuit[]
  workAhead: Pursuit[]
  upcoming: Pursuit[]
}

function byEffectiveDeadline(a: Pursuit, b: Pursuit): number {
  const da = a.effective_deadline ? new Date(a.effective_deadline).getTime() : Infinity
  const db = b.effective_deadline ? new Date(b.effective_deadline).getTime() : Infinity
  return da - db
}

export const usePursuitsStore = defineStore('pursuits', {
  state: () => ({
    /** Normalized by pursuit id. */
    byId: new Map<string, Pursuit>(),
    templates: [] as Template[],
    templatesLoaded: false,
    loading: false,
    loaded: false,
    /** Closed pursuits are fetched separately, on demand — see fetchClosed. */
    closedLoaded: false,
    closedLoading: false,
    error: null as string | null,
    /**
     * Progress through a chunked bulk action. A few hundred pursuits is
     * several round trips, and a button that just says "Saving…" for twenty
     * seconds reads as hung.
     */
    bulkDone: 0,
    bulkTotal: 0,
  }),

  getters: {
    all(state): Pursuit[] {
      return [...state.byId.values()].sort(byEffectiveDeadline)
    },
    active(): Pursuit[] {
      return this.all.filter((p) => p.status === 'active')
    },
    /** Board-visible pursuits: submitted is a live state (awaiting award). */
    open(): Pursuit[] {
      return this.all.filter((p) => p.status === 'active' || p.status === 'submitted')
    },
    closed(): Pursuit[] {
      return this.all.filter(
        (p) => p.status !== 'active' && p.status !== 'submitted',
      )
    },
    templateByKey(state): (key: string) => Template | undefined {
      return (key) => state.templates.find((t) => t.key === key)
    },
    /** Stage metadata for a pursuit: embedded stages, else its template's. */
    stagesFor(): (p: Pursuit) => Stage[] {
      return (p) => p.stages ?? this.templateByKey(p.template_key)?.stages ?? []
    },
    stageName(): (p: Pursuit) => string {
      return (p) => this.stagesFor(p).find((s) => s.key === p.stage)?.name ?? p.stage
    },
    isAtRisk(): (p: Pursuit) => boolean {
      return (p) => atRisk(p, this.stagesFor(p))
    },
    /** Actively-worked pursuits grouped by stage key (submitted pursuits
     * live in the board's own Submitted column, not a stage). */
    byStage(): Record<string, Pursuit[]> {
      const groups: Record<string, Pursuit[]> = {}
      for (const p of this.active) {
        ;(groups[p.stage] ??= []).push(p)
      }
      for (const list of Object.values(groups)) list.sort(byEffectiveDeadline)
      return groups
    },

    /**
     * Today-page buckets, mutually exclusive, in display priority order:
     * overdue → dueToday → actionNeeded → dueThisWeek (at-risk first) →
     * workAhead (at-risk, >7d out) → upcoming.
     */
    todaySections(): TodaySections {
      const notifications = useNotificationsStore()
      const actionPursuitIds = notifications.unreadActionPursuitIds
      const sections: TodaySections = {
        overdue: [],
        dueToday: [],
        actionNeeded: [],
        dueThisWeek: [],
        workAhead: [],
        upcoming: [],
      }
      for (const p of this.active) {
        const days = daysLeft(p.effective_deadline)
        if (days !== null && days < 0) sections.overdue.push(p)
        else if (days === 0) sections.dueToday.push(p)
        else if (p.amendment_review_pending || actionPursuitIds.has(p.id))
          sections.actionNeeded.push(p)
        else if (days !== null && days <= 7) sections.dueThisWeek.push(p)
        else if (this.isAtRisk(p)) sections.workAhead.push(p)
        else sections.upcoming.push(p)
      }
      // Due-this-week: at-risk pursuits float to the top, then by deadline.
      sections.dueThisWeek.sort((a, b) => {
        const ra = this.isAtRisk(a) ? 0 : 1
        const rb = this.isAtRisk(b) ? 0 : 1
        return ra - rb || byEffectiveDeadline(a, b)
      })
      return sections
    },
  },

  actions: {
    /** Mark sent/unsent from the board (drag into/out of Submitted). */
    async setStatus(id: string, status: 'active' | 'submitted') {
      const p = this.byId.get(id)
      const prev = p?.status
      if (p) p.status = status
      try {
        const updated = await api.patchPursuit(id, { status })
        this.upsert(updated)
      } catch (e) {
        if (p && prev) p.status = prev
        throw e
      }
    },

    /**
     * Replace the cache with one slice of the org's pursuits.
     *
     * The board asks for "open". Closed work accumulates without bound — 157
     * rows on FYLO already — and re-fetching it on every board load paid for a
     * scan, a payload and a render of a drawer that starts collapsed.
     */
    async fetchAll(status: 'active' | 'open' | 'all' = 'all') {
      this.loading = true
      this.error = null
      try {
        const res = await api.listPursuits(status)
        const next = new Map<string, Pursuit>()
        for (const p of res.pursuits ?? []) next.set(p.id, p)
        this.byId = next
        this.loaded = true
        this.closedLoaded = status === 'all'
      } catch (e) {
        this.error = e instanceof Error ? e.message : String(e)
      } finally {
        this.loading = false
      }
    },

    /**
     * Pull closed pursuits in and MERGE them, leaving open work untouched.
     *
     * Called when the closed drawer is opened or a search is typed — searching
     * has to reach everything, or a solicitation you archived last month looks
     * like it was deleted. Runs once per session; after that it's a no-op.
     */
    async fetchClosed(): Promise<void> {
      if (this.closedLoaded || this.closedLoading) return
      this.closedLoading = true
      try {
        const res = await api.listPursuits('closed')
        for (const p of res.pursuits ?? []) this.byId.set(p.id, p)
        this.closedLoaded = true
      } catch (e) {
        this.error = e instanceof Error ? e.message : String(e)
      } finally {
        this.closedLoading = false
      }
    },

    async fetchOne(id: string): Promise<Pursuit | null> {
      try {
        const res = await api.getPursuit(id)
        this.byId.set(res.pursuit.id, res.pursuit)
        return res.pursuit
      } catch {
        return null
      }
    },

    async fetchTemplates() {
      if (this.templatesLoaded) return
      try {
        const res = await api.listTemplates()
        this.templates = res.templates ?? []
        this.templatesLoaded = true
      } catch {
        // Non-fatal: stage names fall back to raw keys.
      }
    },

    upsert(p: Pursuit) {
      this.byId.set(p.id, p)
    },

    /**
     * Optimistic stage transition (board drag). The stage flips immediately;
     * a 409 (run_active / bad_transition) or any other failure rolls it back
     * and surfaces the server's message as a toast.
     */
    async transition(id: string, toStage: string): Promise<boolean> {
      const toast = useToast()
      const current = this.byId.get(id)
      if (!current) return false
      if (current.stage === toStage) return true

      const prevStage = current.stage
      this.byId.set(id, { ...current, stage: toStage })
      try {
        const updated = await api.patchPursuit(id, { stage: toStage })
        this.byId.set(id, updated)
        return true
      } catch (e) {
        // Rollback the optimistic move.
        const now = this.byId.get(id)
        if (now) this.byId.set(id, { ...now, stage: prevStage })
        const message =
          e instanceof ApiError ? e.message : 'Failed to move pursuit — please retry.'
        toast.error(message)
        return false
      }
    },

    /** Generic PATCH (deadlines, notes, status…) — non-optimistic. */
    async update(id: string, patch: Parameters<typeof api.patchPursuit>[1]): Promise<Pursuit> {
      const updated = await api.patchPursuit(id, patch)
      this.byId.set(updated.id, updated)
      return updated
    },

    /**
     * One status change across a selection (board bulk no-bid).
     *
     * Not optimistic: with eighty cards the honest thing is to wait for the
     * server's answer, because some of them may come back skipped and pulling
     * them off the board first would make that invisible.
     *
     * Sent in chunks. The endpoint caps a request at 200 so one transaction
     * can't run for half a minute holding locks — but a backlog is exactly the
     * thing that runs past 200, and "select fewer" is the problem this feature
     * exists to solve. So the size limit stays on the server and the client
     * does the arithmetic.
     */
    async bulkStatus(
      input: Parameters<typeof api.bulkPursuitStatus>[0],
    ): Promise<{
      updated: Pursuit[]
      skipped: { id: string; reason: string }[]
      error?: string
    }> {
      const CHUNK = 100
      const updated: Pursuit[] = []
      const skipped: { id: string; reason: string }[] = []
      this.bulkTotal = input.ids.length
      this.bulkDone = 0
      try {
        for (let i = 0; i < input.ids.length; i += CHUNK) {
          const res = await api.bulkPursuitStatus({
            ...input,
            ids: input.ids.slice(i, i + CHUNK),
          })
          for (const p of res.updated ?? []) {
            this.byId.set(p.id, p)
            updated.push(p)
          }
          skipped.push(...(res.skipped ?? []))
          this.bulkDone = Math.min(i + CHUNK, input.ids.length)
        }
        return { updated, skipped }
      } catch (e) {
        // Stop at the failed chunk and report what did land. Pressing on would
        // stack up identical errors and still leave the count a mystery.
        return {
          updated,
          skipped,
          error: e instanceof Error ? e.message : String(e),
        }
      } finally {
        this.bulkTotal = 0
        this.bulkDone = 0
      }
    },

    /**
     * Live workflow.progress SSE: keep the pursuit's "AI working" badge and
     * progress bar current without a refetch per step.
     */
    applyRunProgress(e: WorkflowProgressEvent) {
      const p = this.byId.get(e.pursuit_id)
      if (!p) return
      this.byId.set(p.id, {
        ...p,
        active_run_id: e.run_id,
        run_progress: { step: e.step, total: e.total, detail: e.detail },
      })
    },

    /** SSE dispatch entry point (see stores/events.ts). */
    applyEvent(evt: { type: string; data: unknown }) {
      const data = (evt.data ?? {}) as { pursuit_id?: string }
      switch (evt.type) {
        case 'pursuit.updated':
        case 'action.required':
          if (data.pursuit_id) this.fetchOne(data.pursuit_id)
          break
      }
    },

    reset() {
      this.byId = new Map()
      this.loaded = false
      this.error = null
    },
  },
})
